import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { TrendingUp, Target, Zap, Users } from 'lucide-react';

const benefits = [
  {
    id: 'benefit-lead-gen',
    icon: TrendingUp,
    title: 'Predictable Lead Generation',
    description: 'Systematic traffic generation that delivers consistent results month after month.',
    metric: { value: 340, suffix: '%', label: 'Lead increase' }
  },
  {
    id: 'benefit-conversion',
    icon: Target,
    title: 'Higher Conversion Rates',
    description: 'Optimized funnels that guide prospects through a natural buying journey.',
    metric: { value: 4.8, suffix: 'x', label: 'Conversion boost' }
  },
  {
    id: 'benefit-automation',
    icon: Zap,
    title: 'Automated Nurturing',
    description: 'Set-it-and-forget-it sequences that work around the clock.',
    metric: { value: 24, suffix: '/7', label: 'Always working' }
  },
  {
    id: 'benefit-referrals',
    icon: Users,
    title: 'Compounding Referrals',
    description: 'Turn every customer into a growth engine with systematic referral programs.',
    metric: { value: 67, suffix: '%', label: 'Referral rate' }
  },
];

function AnimatedCounter({ value, suffix, duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const animationRef = useRef(null);
  
  useEffect(() => {
    if (!isInView) return;
    
    let startTime = null;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(value * easeOut);
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isInView, value, duration]);
  
  return (
    <span ref={ref} className="text-4xl md:text-5xl font-bold text-cyan-400">
      {Number.isInteger(value) ? Math.floor(count) : count.toFixed(1)}{suffix}
    </span>
  );
}

export default function WhyItWorksSection() {
  return (
    <section 
      id="why-it-works"
      data-testid="why-it-works-section"
      className="section-padding relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#050a0a] to-[#030303]" />
      
      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4 block">
            The Results
          </span>
          <h2 
            data-testid="why-it-works-headline"
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Why This
            <br />
            <span className="gradient-text-cyan">Works</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-base">
            A systematic approach to marketing delivers measurable, 
            repeatable results that compound over time.
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.id}
              data-testid={`benefit-card-${index}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="group"
            >
              <div className="glass rounded-2xl p-8 h-full card-hover border border-cyan-500/10">
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="flex-1">
                    <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-colors">
                      <benefit.icon className="w-6 h-6 text-cyan-400" />
                    </div>
                    
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                  
                  <div className="flex flex-col items-center md:items-end justify-center min-w-[100px]">
                    <AnimatedCounter 
                      value={benefit.metric.value} 
                      suffix={benefit.metric.suffix}
                    />
                    <span className="text-xs text-gray-500 uppercase tracking-wider mt-1">
                      {benefit.metric.label}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
